import { useEffect } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

import {
  managementRoutes,
  neighbourhoodManagerRoutes,
  neighbourhoodRoutes,
  pcfraRoutes,
  roleAgnosticRoutes,
  routes,
} from '~/client/config/routes';
import { useCurrentUser, useRoleInformation } from '~/client/hooks/storeHooks';

type RouteAccessMap = Record<string, boolean | undefined>;

const getRouteAccessMap = (
  routesObj: Record<string, string>,
  hasAccess?: boolean,
): RouteAccessMap =>
  Object.values(routesObj).reduce(
    (map, route) => ({
      ...map,
      [route]: hasAccess,
    }),
    {},
  );

export const useEnsureHasPageAccess = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentUser = useCurrentUser();
  const { isAdmin, isNeighbourhoodManager, isNeighbourhood, isPcfra } = useRoleInformation();

  const routeAccessMap: RouteAccessMap = {
    ...getRouteAccessMap(managementRoutes, isPcfra),
    ...getRouteAccessMap(neighbourhoodManagerRoutes, isNeighbourhoodManager),
    ...getRouteAccessMap(neighbourhoodRoutes, isNeighbourhood),
    ...getRouteAccessMap(pcfraRoutes, isPcfra),
  };

  const ensureHasPageAccess = () => {
    if (!currentUser) {
      // User will be redirected to login so no action needed
      return;
    }
    const onRoleAgnosticRoute = Object.values(roleAgnosticRoutes).some((route) =>
      matchPath({ path: route, end: true }, pathname),
    );

    if (isAdmin || onRoleAgnosticRoute) {
      return;
    }

    const currentRoute = Object.values(routes).find((route) =>
      matchPath({ path: route, end: true }, pathname),
    );

    if (!currentRoute || !routeAccessMap[currentRoute]) {
      void navigate(routes.notFound, { replace: true });
    }
  };

  useEffect(ensureHasPageAccess, [pathname, currentUser]);
};
